import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FolderKanban, Image, Mail, Wrench, Play, Plus } from 'lucide-react';
import { projectsAPI } from '../../api/projects';
import { galleryAPI } from '../../api/gallery';
import { servicesAPI } from '../../api/services';
import { inquiriesAPI } from '../../api/inquiries';
import { contentAPI } from '../../api/content';
import { getApiBaseUrl } from '../../api/apiConfig';
import './Home.css';

const DEFAULT_WHATSAPP_HREF = 'https://wa.me/254790798042';

const WhatsAppGlyph = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#25D366" aria-hidden>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.883 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const item = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0 },
};

const statCards = [
  {
    key: 'projects',
    label: 'Projects',
    path: '/projects',
    icon: FolderKanban,
    accent: '#8B5CF6',
    linkText: 'View all →',
  },
  {
    key: 'services',
    label: 'Services',
    path: '/services',
    icon: Wrench,
    accent: '#10B981',
    linkText: 'Manage →',
  },
  {
    key: 'newInquiries',
    label: 'New Inquiries',
    path: '/inquiries',
    icon: Mail,
    accent: '#F59E0B',
    linkText: 'View all →',
    urgent: true,
  },
  {
    key: 'gallery',
    label: 'Gallery',
    path: '/gallery',
    icon: Image,
    accent: '#3B82F6',
    linkText: 'Manage →',
  },
];

const Home = () => {
  const [stats, setStats] = useState({
    projects: 0,
    gallery: 0,
    services: 0,
    inquiries: 0,
    newInquiries: 0,
  });
  const [recentInquiries, setRecentInquiries] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);
  const [whatsappHref, setWhatsappHref] = useState(DEFAULT_WHATSAPP_HREF);
  const [loading, setLoading] = useState(true);
  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchAll = async () => {
      try {
        const [projects, gallery, services, inquiries, flat] = await Promise.all([
          projectsAPI.getAll(),
          galleryAPI.getAll(),
          servicesAPI.getAll(),
          inquiriesAPI.getAll(),
          contentAPI.getFlat().catch(() => ({})),
        ]);

        const wa = (flat?.social_whatsapp || '').trim();
        setWhatsappHref(wa || DEFAULT_WHATSAPP_HREF);

        setStats({
          projects: projects?.length || 0,
          gallery: gallery?.length || 0,
          services: (services || []).filter((s) => s.is_active).length,
          inquiries: inquiries?.length || 0,
          newInquiries: (inquiries || []).filter((i) => i.status === 'New').length,
        });
        setRecentInquiries((inquiries || []).slice(0, 5));
        setRecentProjects((projects || []).slice(0, 6));
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    const base = getApiBaseUrl();
    return `${base}${path.startsWith('/') ? path : '/' + path}`;
  };

  const getStatValue = (key) => {
    if (key === 'newInquiries') return stats.newInquiries;
    return stats[key] ?? 0;
  };

  if (loading) {
    return (
      <div className="dashboard-home-loading">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="loading-pulse"
        >
          <div className="loading-dots">
            <span />
            <span />
            <span />
          </div>
          <p>Loading studio...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <motion.div
      className="dashboard-home"
      variants={container}
      initial="hidden"
      animate="show"
    >
      <div className="dashboard-home-header">
        <h1 className="dashboard-title font-display">
          Dashboard
        </h1>
        <p className="dashboard-subtitle">
          Welcome back. Here's what's happening with your website.
        </p>
      </div>

      <div className="stats-grid">
        {statCards.map((card, i) => {
          const Icon = card.icon;
          const value = getStatValue(card.key);
          return (
            <motion.div key={card.key} variants={item}>
              <Link
                to={card.path}
                className={`stat-card ${card.urgent ? 'stat-urgent' : ''}`}
                style={{ '--accent': card.accent }}
              >
                <div className="stat-icon-wrap">
                  <Icon size={24} strokeWidth={2} />
                </div>
                <div className="stat-content">
                  <span className="stat-value">{value}</span>
                  <span className="stat-label">{card.label}</span>
                  {card.key === 'newInquiries' && stats.inquiries > 0 && (
                    <small>{stats.inquiries} total</small>
                  )}
                </div>
                <span className="stat-link">{card.linkText}</span>
              </Link>
            </motion.div>
          );
        })}
      </div>

      <div className="quick-actions">
        <h2 className="section-title">Quick Actions</h2>
        <div className="quick-actions-btns">
          <Link to="/projects/new" className="action-btn primary">
            <Plus size={18} />
            Add Project
          </Link>
          <Link to="/services/new" className="action-btn">
            <Wrench size={18} />
            Add Service
          </Link>
          <Link to="/gallery" className="action-btn">
            <Image size={18} />
            Gallery
          </Link>
          <Link to="/settings" className="action-btn">
            Settings
          </Link>
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="action-btn action-btn-whatsapp"
            title="Open WhatsApp chat (same link as on the public site)"
          >
            <WhatsAppGlyph size={18} />
            WhatsApp
          </a>
        </div>
      </div>

      <div className="dashboard-widgets">
        <div className="widget">
          <div className="widget-header">
            <h2>Recent Inquiries</h2>
            <Link to="/inquiries">View all</Link>
          </div>
          <div className="widget-body">
            {recentInquiries.length === 0 ? (
              <p className="empty-msg">No inquiries yet</p>
            ) : (
              <ul className="inquiry-list">
                {recentInquiries.map((i) => (
                  <li key={i.id} className="inquiry-item">
                    <span className="inquiry-name">{i.sender_name}</span>
                    <span
                      className={`badge ${(i.status || 'New').replace(/\s/g, '-').toLowerCase()}`}
                    >
                      {i.status || 'New'}
                    </span>
                    <span className="inquiry-date">
                      {new Date(i.created_at).toLocaleDateString()}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="widget">
          <div className="widget-header">
            <h2>Recent Projects</h2>
            <Link to="/projects">View all</Link>
          </div>
          <div className="widget-body">
            {recentProjects.length === 0 ? (
              <p className="empty-msg">No projects yet</p>
            ) : (
              <div className="project-cards-grid">
                {recentProjects.map((p) => (
                  <Link
                    key={p.id}
                    to={`/projects/${p.id}`}
                    className="project-card-gallery"
                  >
                    <div className="project-card-media">
                      {p.cover_image ? (
                        <img
                          src={getImageUrl(p.cover_image)}
                          alt={p.title}
                          className="project-card-thumb"
                        />
                      ) : (
                        <div className="project-card-placeholder">
                          <Play size={32} className="placeholder-icon" />
                        </div>
                      )}
                      <div className="project-card-overlay" />
                    </div>
                    <div className="project-card-info">
                      <span className="project-card-title">{p.title}</span>
                      <span className="project-card-meta">{p.category}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

    </motion.div>
  );
};

export default Home;
